import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@teorema/patron';
import { Observable, Subject, Subscription } from 'rxjs';
import { Task, TaskDependency, Ticket, TicketLog, User } from 'src/app/models';
import { utils } from 'src/app/shared';
import { SortPipe } from 'src/app/shared/pipes';
import {
  TcDataService,
  TcTaskService,
  TcTicketService,
  TicketStatusService,
} from 'src/app/shared/services';
import { constants } from '@app/shared/utils/constants';
import { TicketLogTimePipe } from '../../pipes/ticket-log-time.pipe';

@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  standalone: false,
})
export class TasksComponent implements OnInit, OnDestroy {
  @Input() users!: Observable<User[]>;
  @Input() entity?: Ticket;
  @Input() userDes!: User;
  @Input() usersInput = new Subject<string>();
  @Input() loadingUsers = false;
  @Input() highlightTaskId;
  @Input() saving!: boolean;
  @Input() openedLogs: TicketLog[] = [];

  @Output() ticketLogs = new EventEmitter<any>();
  @Output() assembleTicket = new EventEmitter();
  @Output() saved = new EventEmitter<boolean>();

  // @ViewChild("formTask") formTask: NgForm;

  difficulties = constants.ticketcenter.difficulties;
  originalExpectedDateString!: string;
  visibleUnfinishTaskModal = false;
  dependsOnOptions?: Task[] = [];
  expectedDateString!: string;
  dependsOnTasks: Task[] = [];
  unfinishIndex: number = -1;
  wasValidatedTask = false;
  currentTask?: Task;
  private readonly descriptionTextareaBaseRows = 2;
  private readonly descriptionTextareaFocusRows = 4;
  private isDescriptionFocused = false;
  rowNumber = `${this.descriptionTextareaBaseRows}`;
  loading = false;
  oldTask?: Task;

  tasksProgress = {
    loaded: false,
    loading: false,
    data: {
      percentage: null,
    },
  };
  effectiveEffort = 0;
  remainingEffort = 0;
  subscriptions: Subscription[] = [];
  visible = false;
  visibleDeleteModal = false;
  visibleUpdateModal = false;
  formGroup!: FormGroup;

  constructor(
    private ticketService: TcTicketService,
    private ticketLogTimePipe: TicketLogTimePipe,
    private toastService: ToastService,
    private taskService: TcTaskService,
    private dataService: TcDataService,
    private formBuilder: FormBuilder,
    private ticketStatusService: TicketStatusService,
    protected sortPipe: SortPipe,
    private cdr: ChangeDetectorRef
  ) {
    this.formGroup = this.formBuilder.group({
      sequence: [null, [Validators.required]],
      summary: [null],
      description: [null], // Removido Validators.required - validação manual no saveTask()
      ticketStatus: [null, [Validators.required]],
      developer: [null],
      taskEstimatedEffort: [null, [Validators.required]],
      difficulty: [null],
      priority: [null],
      originalExpectedDateString: [null],
      changeTicketExpectedDate: [false],
      expectedDateString: [null],
      dependsOnTasks: [null],
      taskSystems: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (this.currentTask) {
      this.setExpectedDatesFromCurrentTask();
      this.convertTaskDependenciesToDependsOnTasks();
      this.initFormGroup();
    }
    this.subscriptions.push(
      this.dataService.loadTaskProgressEmitter.subscribe((res: boolean) => {
        this.loadTasksProgress(this.entity!, res);
      })
    );
  }

  initFormGroup() {
    this.formGroup.get('sequence')?.setValue(this.currentTask?.sequence);
    this.formGroup.get('summary')?.setValue(this.currentTask?.summary);
    this.formGroup
      .get('description')
      ?.setValue(this.currentTask?.description ? this.currentTask?.description : '');
    this.formGroup.get('ticketStatus')?.setValue(this.currentTask?.status);
    this.formGroup.get('developer')?.setValue(this.currentTask?.user);
    this.formGroup.get('taskEstimatedEffort')?.setValue(this.currentTask?.estimated_effort);
    this.formGroup
      .get('difficulty')
      ?.setValue(this.getDificultyLabel(this.currentTask?.difficulty));
    this.formGroup.get('priority')?.setValue(this.currentTask?.view_index);
    this.formGroup
      .get('originalExpectedDateString')
      ?.setValue(this.formatDate(this.currentTask?.original_expected_date));
    this.formGroup
      .get('changeTicketExpectedDate')
      ?.setValue(this.currentTask?.change_ticket_expected_date);
    this.formGroup
      .get('expectedDateString')
      ?.setValue(this.formatDate(this.currentTask?.expected_date));
    this.formGroup.get('dependsOnTasks')?.setValue(this.dependsOnTasks);

    let systems = this.currentTask?.systems || [];

    if (!systems.length && this.entity?.system?.id) {
      systems = [this.entity!!.system!!];
    }

    this.formGroup.get('taskSystems')?.setValue(systems);

    this.resizeRowNumber();
    this.setRowNumber();
  }

  getDificultyLabel(letter?: string) {
    return constants.ticketcenter.difficulties.find(object => object.value == letter);
  }

  formatDate(dateString: any) {
    if (dateString != null || dateString != undefined) {
      let date = new Date(dateString);
      let dataFormatada = date.toISOString().split('T')[0];

      return dataFormatada;
    } else {
      return null;
    }
  }

  async initNewTask() {
    this.wasValidatedTask = false;
    this.currentTask = new Task();
    this.currentTask.estimated_effort = 1;
    this.currentTask.company_group = this.entity!.company_group;
    this.currentTask.difficulty = this.getDificultyLabel('M').value;
    this.currentTask.change_ticket_expected_date = true;

    if (this.userDes) {
      this.currentTask.user = this.userDes;
    }

    this.loading = true;
    this.currentTask.status =
      (await this.ticketStatusService.getEsforcoDefinidoTicketStatus().catch(e => {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      })) || undefined;
    this.loading = false;

    this.dependsOnTasks = [];
    this.dependsOnOptions = this.entity!.tasks;
    this.setTaskLabels(this.dependsOnOptions!);

    let highestSequence = 0;

    if (this.entity!.tasks) {
      this.entity!.tasks.forEach(t => {
        if (t.sequence! > highestSequence) {
          highestSequence = t.sequence!;
        }
      });
    }

    this.currentTask.sequence = highestSequence + 1;

    if (!this.currentTask.sequence) {
      this.currentTask.sequence = 1;
    }

    this.currentTask.summary = 'tarefa ' + this.currentTask.sequence;
    this.initFormGroup();
  }

  fillValuesWithFormValues() {
    this.currentTask!.sequence = this.formGroup.get('sequence')?.value;
    this.currentTask!.summary = this.formGroup.get('summary')?.value;
    this.currentTask!.description = this.formGroup.get('description')?.value;
    this.currentTask!.status = this.formGroup.get('ticketStatus')?.value;
    this.currentTask!.user = this.formGroup.get('developer')?.value;
    this.currentTask!.estimated_effort = Number(this.formGroup.get('taskEstimatedEffort')?.value);
    this.currentTask!.difficulty = this.formGroup.get('difficulty')?.value?.value;
    this.currentTask!.view_index = this.formGroup.get('priority')?.value;
    this.originalExpectedDateString = this.formGroup.get('originalExpectedDateString')?.value;
    this.currentTask!.change_ticket_expected_date = this.formGroup.get(
      'changeTicketExpectedDate'
    )?.value;
    this.expectedDateString = this.formGroup.get('expectedDateString')?.value;
    this.dependsOnTasks = this.formGroup.get('dependsOnTasks')?.value;
    this.currentTask!.systems = this.formGroup.get('taskSystems')?.value || [];
  }

  saveOldTask() {
    this.oldTask = {
      id: '',
      ticket_status_id:
        this.currentTask!.status && this.currentTask!.status!.id
          ? this.currentTask!.status!.id
          : '',
      estimated_effort: this.currentTask!.estimated_effort,
      expected_date: this.currentTask!.expected_date!,
    };

    if (this.currentTask!.id) {
      this.oldTask!.id = this.currentTask?.id!;
    }
  }

  async saveTask() {
    this.saveOldTask();
    this.fillValuesWithFormValues();

    if (!this.currentTask) {
      this.initNewTask();
      return;
    }

    this.wasValidatedTask = true;

    const descriptionControl = this.formGroup.get('description');
    const descriptionValue = descriptionControl?.value;
    const isDescriptionValid = descriptionValue && descriptionValue.trim().length > 0;

    if (!isDescriptionValid) {
      this.toastService.show({
        color: 'error',
        message: 'Por favor, informe a descrição da tarefa',
      });
      return;
    }

    if (this.formGroup.invalid) {
      this.toastService.show({
        color: 'error',
        message: 'Formulário invalido. Preencha os campos obrigatórios!',
      });
      return;
    }

    if (this.expectedDateString) {
      this.currentTask.expected_date = new Date(`${this.expectedDateString}T00:00:00`);
      this.expectedDateString = '';
    } else {
      this.currentTask.expected_date = null;
    }

    if (this.originalExpectedDateString) {
      this.currentTask.original_expected_date = new Date(
        `${this.originalExpectedDateString}T00:00:00`
      );
      this.originalExpectedDateString = '';
    } else {
      this.currentTask.original_expected_date = null;
    }

    this.convertDependsOnTaskToTaskDependencies();
    let isNew = !this.currentTask!.id;

    if (isNew) {
      this.currentTask.ticket_id = this.entity?.id;
      let response = await this.taskService.save(this.currentTask).catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao salvar a tarefa!',
        });
      });

      if (response?.id) {
        this.saved.emit(true);
        this.currentTask = response!;
        this.entity!.tasks!.push(this.currentTask);
        this.setEfforts();
        this.updateTicketFields(isNew);

        this.toastService.show({
          color: 'success',
          message: 'Tarefa salva com sucesso!',
        });
        this.cdr.markForCheck();
      }
    } else {
      let response = await this.taskService
        .patch(this.currentTask.id, this.currentTask)
        .catch(() => {
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao atualizar a tarefa!',
          });
        });

      if (response?.id) {
        this.saved.emit(true);
        this.currentTask = response!;
        this.updateTicketFields(isNew);

        this.toastService.show({
          color: 'success',
          message: 'Tarefa atualizada com sucesso!',
        });
        this.cdr.markForCheck();
      }

      for (let i = 0; i < this.entity!.tasks!.length; i++) {
        let task = this.entity!.tasks![i];
        if (task.id == this.currentTask.id) {
          this.entity!.tasks![i] = this.currentTask;
          break;
        }
      }
    }

    this.cancelEdit();
  }

  async updateTicketFields(skipValidation: boolean) {
    let ticket = new Ticket();
    let canPatch = false;
    let task = this.currentTask;

    if (!this.currentTask?.id && skipValidation && this.entity?.tasks?.length) {
      task = this.entity!.tasks[this.entity!.tasks!.length - 1];
    }

    if (!task?.id) {
      return;
    }

    if (skipValidation || this.oldTask!.estimated_effort != task?.estimated_effort) {
      let effort;

      if (!skipValidation) {
        effort = this.entity?.estimated_effort;
        this.setEfforts();
      }

      if (effort! != this.entity?.estimated_effort!) {
        ticket.estimated_effort = this.entity!.estimated_effort;
        canPatch = true;
      }
    }

    if (
      skipValidation ||
      (this.oldTask!.ticket_status_id != task?.status!.id &&
        task!.status!.id != this.entity?.ticket_status_id)
    ) {
      ticket.ticket_status_id = task!.status!.id;
      this.entity!.ticket_status_id = task!.status!.id;
      this.entity!.status = task!.status;
      canPatch = true;
    }

    let oldDate = this.entity!.expected_date!;
    this.entity!.expected_date = this.taskService.getLastExpectedDate(this.entity);
    if (skipValidation || this.getTime(oldDate) != this.getTime(this.entity!.expected_date!)) {
      ticket.expected_date = this.entity!.expected_date;
      canPatch = true;
    }

    if (canPatch) {
      ticket.id = this.entity!.id;

      let response = await this.ticketService.patchMultiple([ticket]).catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao atualizar o ticket!',
        });
      });

      if (response && response.length) {
        this.assembleTicket.emit(response[0]);

        this.toastService.show({
          color: 'success',
          message: 'Chamado atualizado com sucesso!',
        });
      }
    }

    this.oldTask = undefined;
  }

  getTime(date?: Date) {
    return new Date(date!).getTime();
  }

  showUpdateForm(task: Task) {
    this.currentTask = task;
    this.setExpectedDatesFromCurrentTask();
    this.convertTaskDependenciesToDependsOnTasks();
    this.dependsOnOptions = this.entity!.tasks!.filter(
      t => !this.currentTask!.id || t.id !== this.currentTask!.id
    );
    this.setTaskLabels(this.dependsOnOptions);

    setTimeout(() => {
      this.descriptionChanged();
    }, 1);

    this.initFormGroup();
  }

  deleteModalSwitch(task?: Task) {
    this.currentTask = task;
    this.visibleDeleteModal = !this.visibleDeleteModal;
  }

  async deleteTask(task?: Task) {
    let index = this.entity!.tasks!.findIndex(t => t.id === task!.id);

    if (index > -1) {
      this.saving = true;
      let response = await this.taskService
        .delete(task!.id)
        .catch(() => {
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao excluir a tarefa!',
          });
        })
        .finally(() => {
          this.saving = false;
        });

      if (response) {
        this.saved.emit(true);
        let deletedTask: Task = this.entity!.tasks!.splice(index, 1)[0];

        this.setEfforts();
        this.ajustTaskDependecies(deletedTask.id);
        this.updateTicketFields(true);

        this.toastService.show({
          color: 'success',
          message: 'Tarefa excluída com sucesso!',
        });
        this.cdr.markForCheck();
      }
    }

    this.deleteModalSwitch(undefined);
  }

  ajustTaskDependecies(deleteId: string) {
    this.entity?.tasks?.forEach(task => {
      if (task.task_dependencies?.length) {
        task.task_dependencies = task.task_dependencies.filter(taskDependency => {
          return taskDependency.depends_on_task!.id != deleteId;
        });
      }
    });
  }

  // ajustExpectedDate() {
  //   if (this.entity!.tasks?.length) {
  //     let tasks = [...this.entity!.tasks!];
  //     tasks = this.sortPipe.transform(tasks, "expected_date");

  //     this.entity!.expected_date = tasks![tasks!.length - 1].expected_date;
  //   }
  // }

  isDelayed(task) {
    return this.dataService.isDelayed(
      this.ticketService.getExpectedDate({ task, ticket: this.entity })
    );
  }

  getStatusClass(status) {
    return this.dataService.getTicketStatusClass(status);
  }

  convertDependsOnTaskToTaskDependencies() {
    if (this.dependsOnTasks && this.dependsOnTasks.length) {
      if (!this.currentTask!.task_dependencies) {
        this.currentTask!.task_dependencies = [];
      }

      this.dependsOnTasks.forEach(dependsOnTask => {
        if (
          !this.currentTask!.task_dependencies!.filter(taskDependency => {
            return (
              taskDependency.depends_on_task &&
              taskDependency.depends_on_task.id === dependsOnTask.id
            );
          }).length
        ) {
          let taskDependency = new TaskDependency();
          taskDependency.depends_on_task = dependsOnTask;

          this.currentTask!.task_dependencies!.push(taskDependency);
        }
      });
    }

    this.removeDeletedTaskDependencies();
  }

  removeDeletedTaskDependencies() {
    if (this.dependsOnTasks && this.currentTask!.task_dependencies) {
      let newTaskDependencies: any[] = [];

      this.currentTask!.task_dependencies.forEach(taskDependency => {
        if (
          taskDependency.depends_on_task &&
          this.dependsOnTasks!.filter(dependsOnTask => {
            return dependsOnTask.id === taskDependency.depends_on_task!.id;
          }).length
        ) {
          newTaskDependencies.push(taskDependency);
        }
      });

      this.currentTask!.task_dependencies = newTaskDependencies;
    }
  }

  convertTaskDependenciesToDependsOnTasks() {
    this.dependsOnTasks = [];

    if (this.currentTask!.task_dependencies) {
      this.dependsOnTasks = this.currentTask!.task_dependencies.filter(
        td => td && td.depends_on_task !== undefined
      ).map(td => {
        return td!.depends_on_task!;
      });
    }

    this.setTaskLabels(this.dependsOnTasks);
  }

  setTaskLabels(tasks: Task[]) {
    if (tasks) {
      tasks.forEach(task => {
        let text = task.summary;

        if (!text || !text.length) {
          text = task.description;

          if (text!.length > 30) {
            text = text!.substring(0, 26) + '...';
          }
        }

        task.label = (task.sequence || 'Sem sequência') + ' - ' + text;
      });
    }
  }

  async finish(index, confirmed) {
    let task = this.entity!.tasks![index];

    if (!task.finished && this.getTaskOpenedLogsFromTaskUser(task).length) {
      this.toastService.show({
        color: 'warn',
        message:
          'O usuário desenvolvedor ainda está apontando e deve encerrar o apontamento antes que se possa concluir a tarefa!',
      });
      return;
    }

    if (task.finished && !confirmed) {
      this.unfinishIndex = index;
      this.unfinishTaskModalSwitch();
      return;
    }

    let _task = {
      id: task.id,
      finished: task.finished ? false : true,
    };

    this.saving = true;
    let response = await this.taskService.patch(_task.id, _task).catch(() => {
      this.toastService.show({
        color: 'error',
        message: 'Ocorreu um erro ao salvar',
      });
    });

    if (response?.id) {
      this.saved.emit(true);

      if (response.finished) {
        this.toastService.show({
          color: 'success',
          message: 'Tarefa concluída com sucesso',
        });
      } else {
        this.toastService.show({
          color: 'success',
          message: 'Tarefa marcada como não concluída',
        });
      }

      this.entity!.tasks![index].finished = response.finished;
      this.setEfforts();
      this.cdr.markForCheck();
    }

    this.saving = false;
  }

  unfinishConfirm() {
    this.unfinishTaskModalSwitch();
    this.finish(this.unfinishIndex, true);
  }

  unfinishTaskModalSwitch() {
    this.visibleUnfinishTaskModal = !this.visibleUnfinishTaskModal;
  }

  getTaskOpenedLogsFromTaskUser(task: Task) {
    if (task.user && this.openedLogs) {
      return this.openedLogs.filter(
        log => log.task?.id === task.id && log.user!.id === task.user!.id
      );
    }

    return [];
  }

  getTaskOpenedLogs(task: Task) {
    if (this.openedLogs) {
      return this.openedLogs.filter(log => log.task?.id === task.id);
    }

    return [];
  }

  setExpectedDatesFromCurrentTask() {
    if (this.currentTask!.expected_date) {
      this.expectedDateString = new Date(this.currentTask!.expected_date).toJSON().slice(0, 10);
    }
    if (this.currentTask!.original_expected_date) {
      this.originalExpectedDateString = new Date(this.currentTask!.original_expected_date)
        .toJSON()
        .slice(0, 10);
    }
  }

  async loadTasksProgress(entity: Ticket, reload?: boolean) {
    if (!this.entity) {
      this.entity = entity;
    }

    if (
      (!this.tasksProgress.loaded || reload) &&
      !this.tasksProgress.loading &&
      this.entity!.tasks?.length
    ) {
      this.tasksProgress.loading = true;
      let response = await this.taskService.getTasksProgress(this.entity!.id).catch(() => {
        this.toastService.show({
          color: 'error',
          message: 'Não foi possível verificar o progresso das tarefas!',
        });
      });
      this.tasksProgress.loading = false;

      if (response && typeof response === 'object') {
        let taskIds = Object.keys(response);

        if (taskIds?.length) {
          for (let taskId in response) {
            if (response[taskId].estimatedMinutes) {
              response[taskId].estimatedTime =
                this.ticketLogTimePipe.getDaysHoursAndMinutesFromTime(
                  response[taskId].estimatedMinutes * 60 * 1000
                );
            }

            if (response[taskId].effectiveMinutes) {
              response[taskId].effectiveTime =
                this.ticketLogTimePipe.getDaysHoursAndMinutesFromTime(
                  response[taskId].effectiveMinutes * 60 * 1000
                );
            }
          }
        }

        this.tasksProgress.data = response;
        this.tasksProgress.loaded = true;
        this.cdr.markForCheck();
      }
    }
  }

  getTaskProgressBarType(task: Task) {
    let p = this.tasksProgress.data![task.id].percentage;

    if (p === 100) {
      return 'success';
    } else if (p > 100) {
      return 'warning';
    } else {
      return 'primary';
    }
  }

  getTaskProgressBarLabel(task: Task) {
    return `${this.tasksProgress.data[task.id].effectiveTime} / ${
      this.tasksProgress.data[task.id].estimatedTime
    }   (${this.tasksProgress.data[task.id].percentage}%)`;
  }

  openBulkUpdateModal() {
    this.visibleUpdateModal = false;

    setTimeout(() => {
      this.visibleUpdateModal = true;
    }, 1);
  }

  async confirmBulkUpdate(content) {
    let expectedDate;

    if (content.expectedDateString) {
      expectedDate = new Date(`${content.expectedDateString}T00:00:00`);
      this.expectedDateString = '';
    }

    const selectedTaskIds: Set<string> = content.selectedTaskIds || new Set();
    const tasksToUpdate = (this.entity!.tasks || []).filter((task: Task) =>
      selectedTaskIds.has(task.id)
    );

    if (!tasksToUpdate.length) {
      this.toastService.show({
        color: 'warn',
        message: 'Nenhuma tarefa selecionada para atualizar',
      });
      return;
    }

    tasksToUpdate.forEach((task: Task) => {
      if (content.status?.id) {
        task.status = content.status;
        this.entity!.status = task.status;
      }

      if (content.removeExpectedDate) {
        task.expected_date = null;
      } else if (expectedDate) {
        task.expected_date = expectedDate;
      }

      if (content.user?.id) {
        task.user = content.user;
      }
    });

    this.loading = true;
    let response = await this.taskService
      .patchMultiple(
        tasksToUpdate.map((task: Task) => {
          return {
            id: task.id,
            ticket_status_id: task.status!.id,
            expected_date: task.expected_date,
            user_id: task.user!.id,
          };
        })
      )
      .catch(e => {
        console.error(e);
        this.toastService.show({
          color: 'error',
          message: 'Houve um erro ao atualizar as tarefas!',
        });
      });
    this.loading = false;

    if (response?.length) {
      this.saved.emit(true);
      this.updateTicketFields(true);
      this.toastService.show({
        color: 'success',
        message: 'Tarefas atualizadas com sucesso!',
      });
      this.cdr.markForCheck();
    }
  }

  setEfforts() {
    if (this.entity!.tasks?.length) {
      let estimatedEffort = 0;
      this.effectiveEffort = 0;

      this.entity!.tasks.forEach(task => {
        if (task.estimated_effort) {
          let temp = Number(task.estimated_effort);

          if (temp && !isNaN(temp)) {
            estimatedEffort += temp;

            if (task.finished) {
              this.effectiveEffort += temp;
            }
          }
        }
      });

      this.remainingEffort = estimatedEffort - this.effectiveEffort;

      if (this.dataService.shouldRecalculateTaskEffort) {
        this.entity!.estimated_effort = estimatedEffort;
      }
    } else {
      this.remainingEffort = 0;

      if (this.dataService.shouldRecalculateTaskEffort) {
        this.entity!.estimated_effort = 0;
      }
    }
  }

  setRowNumber() {
    this.updateDescriptionRows();
  }

  cancelEdit() {
    this.currentTask = undefined;
    this.resizeRowNumber();
  }

  resizeRowNumber() {
    this.isDescriptionFocused = false;
    this.updateDescriptionRows();
  }

  descriptionChanged() {
    this.setRowNumber();
  }

  onDescriptionFocus(): void {
    this.isDescriptionFocused = true;
    this.updateDescriptionRows();
  }

  onDescriptionBlur(): void {
    this.isDescriptionFocused = false;
    this.updateDescriptionRows();
  }

  private updateDescriptionRows(): void {
    const descriptionValue = this.formGroup?.get('description')?.value || '';
    const lineBreaks = (descriptionValue.match(/\n/g) || []).length + 1;
    const minRows = this.isDescriptionFocused
      ? this.descriptionTextareaFocusRows
      : this.descriptionTextareaBaseRows;
    const nextRows = Math.max(minRows, lineBreaks);
    this.rowNumber = `${nextRows}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => {
      s.unsubscribe();
    });
  }

  getTaskLogButtons(task: Task): any[] {
    if (task.systems?.length && task.systems?.length > 1) {
      return [null, ...task.systems];
    } else {
      return [null];
    }
  }

  isSprintCanWrite() {
    return utils.checkAccess(['Sprint'], true, true).result;
  }
}
